import React, { useEffect, useRef } from 'react';
import { trackEventWithUTM } from '../../utils/analytics';
import { useCityPricing } from '../../contexts/CityPricingContext';
import { availableCities } from '../../services/pricingService';
import OptimizedImage from '../OptimizedImage';
import './HeroBanner.css';

// ─── Types ────────────────────────────────────────────────────────────────────
interface HeroBannerProps {
  /** Called when "Explore Services ↓" is clicked – scrolls to the services section */
  onScrollToServices?: () => void;
}

// ─── Brand copy — edit here, no JSX changes needed ───────────────────────────
const COPY = {
  eyebrow: "India's Premium Vedic Ritual Platform",
  headline: ['Authentic ', 'Kashi Pooja', ' performed at Your Home'],
  useCases: ['Griha Pravesh', 'Marriage', 'Health', 'Dosha Nivaran'],
  bullets: [
    'Verified Kashi Acharyas',
    'Curated samagri',
    'Guided rituals + support',
  ],
  location: '• Serving At Home in Delhi NCR • at our Kashi site — participate remotely/In-person from anywhere',
  primaryCta: { label: 'Book Pooja at Home', scrollTo: 'popular-pujas' },
  secondaryCta: { label: 'View Popular Poojas', scrollTo: 'popular-pujas' },
  kashiHook: {
    text: 'Want your pooja performed in Kashi?',
    subtext: "We perform it at our Kashi site - you simply book.",
    ctaLabel: 'Explore Kashi Poojas →',
    scrollTo: 'kashi-pujas',   // ← points to the KashiPujas section
  },
};

// ─── Trust-bar items ──────────────────────────────────────────────────────────
const TRUST_ITEMS = [
  { icon: '✦', text: 'Kashi Pooja for NRIs' },
  { icon: '✦', text: 'Dedicated support' },
  { icon: '✦', text: 'On-Time Arrival Guarantee' },
  { icon: '✦', text: 'All samagri included' },
];

// ─── Differentiator pillars ───────────────────────────────────────────────────
const PILLARS = [
  {
    icon: (
      // Shield / Verified badge
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    title: 'Kashi-Certified Pandits',
    desc: 'Every Acharya is from a traditional Kashi lineage - verified, trained, and practising for decades.',
  },
  {
    icon: (
      // Book / Guided vidhi
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
      </svg>
    ),
    title: 'Guided Rituals, Not Just Booking',
    desc: 'Receive the full vidhi - samagri curation, muhurat guidance, ritual steps, and a post-puja follow-up.',
  },
  {
    icon: (
      // Star / outcome-driven
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    title: 'Outcome-Driven Experience',
    desc: 'We measure success by your peace of mind - transparent packages, no surprises, no shortcuts.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

const HeroBanner: React.FC<HeroBannerProps> = ({ onScrollToServices }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const { setSelectedCity } = useCityPricing();

  // Helpers — find city objects once so click handlers are concise
  const delhiCity = availableCities.find((c) => c.code === 'DELHI')!;
  const kashiCity = availableCities.find((c) => c.code === 'PLATFORM_SITE')!

  // Fire once when hero scrolls into view (≥30% visible)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEventWithUTM('homepage_section_viewed', 'homepage', 'hero_banner');
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleScrollCta = () => {
    trackEventWithUTM('homepage_hero_cta_click', 'hero_banner', 'view_popular_poojas');
    const target = document.getElementById(COPY.secondaryCta.scrollTo);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
<section ref={sectionRef} className="hero-banner" aria-label="Hero — Antaryatra spiritual services">

      {/* ── Background ── */}
      <div className="hero-banner__bg" aria-hidden="true">
        <OptimizedImage
          src="/images/carousel/slide-2.jpg"
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/puja_service.jpg';
          }}
        />
      </div>
      <div className="hero-banner__overlay" aria-hidden="true" />

      {/* ── Main content ── */}
      <div className="hero-banner__body">
        <div className="hero-banner__inner">

          {/* Eyebrow */}
          {/* <p className="hero-banner__eyebrow">
            <span className="hero-banner__eyebrow-dot" aria-hidden="true" />
            {COPY.eyebrow}
          </p> */}

          {/* Headline */}
          <h1 className="hero-banner__headline">
            {COPY.headline[0]}
            <span className="hero-banner__headline-accent">{COPY.headline[1]}</span>
            {COPY.headline[2]}
          </h1>

          {/* Use-case line */}
          <p className="hero-banner__use-cases">
            <span className="hero-banner__use-cases-label">Perfect for:</span>
            {COPY.useCases.map((uc, i) => (
              <React.Fragment key={uc}>
                <span className="hero-banner__use-case">{uc}</span>
                {i < COPY.useCases.length - 1 && (
                  <span className="hero-banner__use-case-dot" aria-hidden="true">•</span>
                )}
              </React.Fragment>
            ))}
          </p>

          {/* Bullets + location */}
          <ul className="hero-banner__bullets" aria-label="Key features">
            {COPY.bullets.map((b) => (
              <li key={b} className="hero-banner__bullet">
                <span className="hero-banner__bullet-check" aria-hidden="true">✔</span>
                {b}
              </li>
            ))}
          </ul>
          <p className="hero-banner__location">{COPY.location}</p>
          <div className="hero-banner__ctas">
            <button
              className="hero-banner__cta-primary"
              aria-label="Book a puja now"
              onClick={() => {
                trackEventWithUTM('homepage_hero_cta_click', 'hero_banner', 'book_a_pooja');
                setSelectedCity(delhiCity);
                const el = document.getElementById(COPY.primaryCta.scrollTo);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {COPY.primaryCta.label}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>

            <button
              className="hero-banner__cta-secondary"
              onClick={handleScrollCta}
              aria-label="Explore all puja services"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              {COPY.secondaryCta.label}
            </button>
          </div>

          {/* Kashi hook */}
          <div className="hero-banner__kashi-hook">
            <div className="hero-banner__kashi-hook-text">
              <span className="hero-banner__kashi-hook-icon" aria-hidden="true"></span>
              <div>
                <strong>{COPY.kashiHook.text}</strong>
                <span>{COPY.kashiHook.subtext}</span>
              </div>
            </div>
            <button
              className="hero-banner__kashi-hook-cta"
              onClick={() => {
                trackEventWithUTM('homepage_hero_cta_click', 'hero_banner', 'explore_kashi_poojas');
                setSelectedCity(kashiCity);
                const el = document.getElementById(COPY.kashiHook.scrollTo);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {COPY.kashiHook.ctaLabel}
            </button>
          </div>

          {/* Trust bar */}
          <div className="hero-banner__trust" role="list" aria-label="Platform highlights">
            {TRUST_ITEMS.map((item, i) => (
              <React.Fragment key={i}>
                <div className="hero-banner__trust-item" role="listitem">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  {item.text}
                </div>
                {i < TRUST_ITEMS.length - 1 && (
                  <div className="hero-banner__trust-divider" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scroll cue ── */}
      <button
        className="hero-banner__scroll-cue"
        onClick={handleScrollCta}
        aria-label="Scroll down to services"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* ── Three differentiator pillars ── */}
      <div className="hero-banner__pillars" role="list" aria-label="Why Antaryatra">
        {PILLARS.map((p, i) => (
          <div className="hero-banner__pillar" role="listitem" key={i}>
            <div className="hero-banner__pillar-icon" aria-hidden="true">
              {p.icon}
            </div>
            <div className="hero-banner__pillar-text">
              <h4>{p.title}</h4>
              <p>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

    </section>
  );
};

export default HeroBanner;
